import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Award, Download, Filter, RefreshCw, Search, Timer, Trophy, Users } from "lucide-react";
import {
  exportLmsGlobalReport,
  fetchLmsGlobalReport,
  fetchLmsLearnerReport,
} from "../../../../../services/moduleLmsReportingApi";
import LmsContentDetailPanel from "./LmsContentDetailPanel";
import LmsGlobalReportingTable from "./LmsGlobalReportingTable";
import LmsLearnerDetailModal from "./LmsLearnerDetailModal";
import "./LmsGlobalReporting.css";

const formatTime = (seconds) => {
  if (!seconds) return "0 min";
  const minutes = Math.floor(seconds / 60);
  return minutes < 60 ? `${minutes} min` : `${Math.floor(minutes / 60)}h ${minutes % 60}min`;
};

const formatScore = (value) => (value == null ? "-" : Math.round(value * 10) / 10);

const contentTypes = ["PDF", "VIDEO", "ARTICLE", "QUIZ", "EVALUATION", "SCORM", "CERTIFICAT", "EVENEMENT"];

export default function LmsGlobalReportingPage() {
  const { moduleId } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [scoreFilter, setScoreFilter] = useState("all");
  const [progressFilter, setProgressFilter] = useState("all");
  const [contentType, setContentType] = useState("all");
  const [certificate, setCertificate] = useState("all");
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadReport = async () => {
    setLoading(true);
    setError("");
    try {
      setReport(await fetchLmsGlobalReport(moduleId));
    } catch (err) {
      setError(err?.response?.data?.message || "Impossible de charger le reporting LMS.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReport();
  }, [moduleId]);

  const filteredRows = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return (report?.learners || []).filter((row) => {
      const text = `${row.fullName || ""} ${row.email || ""} ${row.status || ""}`.toLowerCase();
      const matchesText = !normalized || text.includes(normalized);
      const matchesStatus = status === "all" || row.status === status;
      const matchesCertificate =
        certificate === "all" || (certificate === "generated" ? row.certificateGenerated : !row.certificateGenerated);
      const matchesScore =
        scoreFilter === "all" ||
        (scoreFilter === "low" && (row.score || 0) < 50) ||
        (scoreFilter === "medium" && (row.score || 0) >= 50 && (row.score || 0) < 80) ||
        (scoreFilter === "high" && (row.score || 0) >= 80);
      const matchesProgress =
        progressFilter === "all" ||
        (progressFilter === "late" && (row.progressPercent || 0) < 50) ||
        (progressFilter === "active" && (row.progressPercent || 0) >= 50 && (row.progressPercent || 0) < 100) ||
        (progressFilter === "done" && (row.progressPercent || 0) >= 100);
      const matchesType =
        contentType === "all" || (row.contents || []).some((content) => content.type === contentType && content.status !== "NOT_STARTED");
      return matchesText && matchesStatus && matchesCertificate && matchesScore && matchesProgress && matchesType;
    });
  }, [report, query, status, scoreFilter, progressFilter, contentType, certificate]);

  const openDetail = async (userId) => {
    setDetailLoading(true);
    setDetail(null);
    try {
      setDetail(await fetchLmsLearnerReport(moduleId, userId));
    } finally {
      setDetailLoading(false);
    }
  };

  const downloadExport = async () => {
    const response = await exportLmsGlobalReport(moduleId);
    const href = URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.href = href;
    link.download = `lms-reporting-module-${moduleId}.csv`;
    link.click();
    URL.revokeObjectURL(href);
  };

  return (
    <div className="lms-report-page">
      <div className="lms-report-header">
        <div>
          <span>Reporting global</span>
          <h1>{report?.moduleName || "Progression module"}</h1>
        </div>
        <div className="lms-report-actions">
          <button type="button" onClick={loadReport}>
            <RefreshCw size={16} />
            Actualiser
          </button>
          <button type="button" onClick={downloadExport} disabled={!report}>
            <Download size={16} />
            Export CSV
          </button>
        </div>
      </div>

      {error && <div className="lms-report-alert">{error}</div>}

      <div className="lms-kpi-grid">
        <div className="lms-kpi">
          <Users size={18} />
          <span>Apprenants</span>
          <strong>{report?.totalLearners || 0}</strong>
        </div>
        <div className="lms-kpi">
          <RefreshCw size={18} />
          <span>Actifs</span>
          <strong>{report?.activeLearners || 0}</strong>
        </div>
        <div className="lms-kpi">
          <Trophy size={18} />
          <span>Terminés</span>
          <strong>{report?.completedLearners || 0}</strong>
        </div>
        <div className="lms-kpi">
          <Award size={18} />
          <span>Score moyen</span>
          <strong>{formatScore(report?.averageScore)}</strong>
        </div>
        <div className="lms-kpi">
          <Timer size={18} />
          <span>Temps moyen</span>
          <strong>{formatTime(report?.averageTimeSpentSeconds)}</strong>
        </div>
      </div>

      <div className="lms-report-layout">
        <LmsContentDetailPanel sections={report?.sections} blockingContents={report?.blockingContents} />

        <section className="lms-report-main">
          <div className="lms-toolbar">
            <label>
              <Search size={16} />
              <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Rechercher collaborateur, email, statut" />
            </label>
            <div className="lms-filter-group">
              <Filter size={15} />
              <select value={status} onChange={(event) => setStatus(event.target.value)}>
                <option value="all">Tous statuts</option>
                <option value="NOT_STARTED">Non commence</option>
                <option value="IN_PROGRESS">En cours</option>
                <option value="COMPLETED">Termine</option>
                <option value="FAILED">Echoue</option>
              </select>
              <select value={progressFilter} onChange={(event) => setProgressFilter(event.target.value)}>
                <option value="all">Toute progression</option>
                <option value="late">&lt; 50%</option>
                <option value="active">50-99%</option>
                <option value="done">100%</option>
              </select>
              <select value={scoreFilter} onChange={(event) => setScoreFilter(event.target.value)}>
                <option value="all">Tous scores</option>
                <option value="low">&lt; 50</option>
                <option value="medium">50-79</option>
                <option value="high">80+</option>
              </select>
              <select value={contentType} onChange={(event) => setContentType(event.target.value)}>
                <option value="all">Tous contenus</option>
                {contentTypes.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
              <select value={certificate} onChange={(event) => setCertificate(event.target.value)}>
                <option value="all">Certificats</option>
                <option value="generated">Generes</option>
                <option value="missing">Non générés</option>
              </select>
            </div>
          </div>

          {loading ? (
            <div className="lms-report-empty">Chargement du reporting LMS...</div>
          ) : (
            <LmsGlobalReportingTable rows={filteredRows} onOpenDetail={openDetail} />
          )}
        </section>
      </div>

      <LmsLearnerDetailModal detail={detail} loading={detailLoading} onClose={() => setDetail(null)} />
    </div>
  );
}
